// frontend/src/components/profile/WalletDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { walletApi } from '../../api/wallet';
import helpers from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext'; 
import BankTransferInfo from '../common/BankTransferInfo';
import { FaWallet, FaMoneyBillWave, FaHistory, FaSpinner, FaUniversity, FaInfoCircle, FaChevronDown, FaSearch, FaTimes, FaCoins, FaSync } from 'react-icons/fa';

// --- FALLBACK BANK LIST (Safety Net) ---
const FALLBACK_BANKS = [
    { id: 1, code: "044", name: "Access Bank" },
    { id: 2, code: "023", name: "Citibank Nigeria" },
    { id: 3, code: "063", name: "Diamond Bank" },
    { id: 4, code: "050", name: "Ecobank Nigeria" },
    { id: 5, code: "070", name: "Fidelity Bank" },
    { id: 6, code: "011", name: "First Bank of Nigeria" },
    { id: 7, code: "214", name: "First City Monument Bank" },
    { id: 8, code: "058", name: "Guaranty Trust Bank" },
    { id: 9, code: "030", name: "Heritage Bank" },
    { id: 10, code: "301", name: "Jaiz Bank" },
    { id: 11, code: "082", name: "Keystone Bank" },
    { id: 12, code: "014", name: "MainStreet Bank" },
    { id: 13, code: "076", name: "Skye Bank" },
    { id: 14, code: "221", name: "Stanbic IBTC Bank" },
    { id: 15, code: "068", name: "Standard Chartered Bank" },
    { id: 16, code: "232", name: "Sterling Bank" },
    { id: 17, code: "032", name: "Union Bank of Nigeria" },
    { id: 18, code: "033", name: "United Bank for Africa" },
    { id: 19, code: "215", name: "Unity Bank" },
    { id: 20, code: "035", name: "Wema Bank" },
    { id: 21, code: "057", name: "Zenith Bank" },
    { id: 22, code: "100004", name: "OPay" },
    { id: 23, code: "100002", name: "Paga" },
    { id: 24, code: "100011", name: "Kuda Bank" },
    { id: 25, code: "100018", name: "Moniepoint" }
];

function WalletDashboard() {
  const { user, reloadUser } = useAuth(); 
  const [balance, setBalance] = useState({ balance: 0, total_earned: 0, pending_payouts: 0 });
  const [transactions, setTransactions] = useState([]);
  const [banks, setBanks] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Withdraw Form
  const [withdrawData, setWithdrawData] = useState({
      amount: '',
      bank_code: '', 
      bank_name: '', 
      account_number: '',
      account_name: '', 
      notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // --- SEARCHABLE DROPDOWN STATE ---
  const [isBankListOpen, setIsBankListOpen] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const init = async () => {
        if (reloadUser) await reloadUser();
        fetchData();
    };
    init();
    
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsBankListOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balData, txData, banksData] = await Promise.all([
          walletApi.getBalance(),
          walletApi.getTransactions(),
          walletApi.getBanks().catch(e => [])
      ]);
      
      setBalance({
          balance: balData?.balance || 0,
          total_earned: balData?.total_earned || 0,
          pending_payouts: balData?.pending_payouts || 0
      });
      
      setTransactions(txData);
      
      if (Array.isArray(banksData) && banksData.length > 0) {
          setBanks(banksData.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
          setBanks(FALLBACK_BANKS.sort((a, b) => a.name.localeCompare(b.name)));
      }

    } catch (err) {
      console.error("Failed to load wallet data", err);
      setBanks(FALLBACK_BANKS);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
      setIsRefreshing(true);
      if (reloadUser) await reloadUser();
      await fetchData();
      setIsRefreshing(false);
  };

  const handleSelectBank = (bank) => {
      setWithdrawData({
          ...withdrawData,
          bank_code: bank.code,
          bank_name: bank.name
      });
      setIsBankListOpen(false);
      setBankSearchTerm('');
  };

  const handleWithdraw = async (e) => {
      e.preventDefault();
      const amount = parseFloat(withdrawData.amount);
      
      if (amount > balance.balance) return alert("Insufficient funds.");
      if (amount < 1000) return alert("Minimum withdrawal is NGN 1,000.");
      if (!withdrawData.bank_code) return alert("Please select a bank.");

      setSubmitting(true);
      try {
          await walletApi.requestWithdrawal({ ...withdrawData, amount });
          alert("Withdrawal request submitted successfully!");
          setShowWithdrawModal(false);
          setWithdrawData({ amount: '', bank_code: '', bank_name: '', account_number: '', account_name: '', notes: '' });
          fetchData(); 
      } catch (err) {
          alert(err.data?.message || err.message || "Failed to request withdrawal.");
      } finally {
          setSubmitting(false);
      }
  };

  const filteredBanks = banks.filter(bank => 
      bank.name.toLowerCase().includes(bankSearchTerm.toLowerCase())
  );

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Wallet...</div>;

  return (
    <div>
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FaWallet className="text-primary" /> My Wallet
              </h2>
              <button 
                  onClick={handleManualRefresh} 
                  className="text-gray-500 hover:text-primary p-2 rounded-full transition-colors"
                  title="Refresh Balance"
              >
                  <FaSync className={isRefreshing ? "animate-spin" : ""} />
              </button>
          </div>
          <button 
              onClick={() => setShowWithdrawModal(true)}
              className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700 flex items-center justify-center gap-2 shadow-md transition-transform hover:scale-105"
          >
              <FaMoneyBillWave /> Request Payout
          </button>
      </div>

      {/* CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          
          {/* --- SPENDING BALANCE CARD (FIXED CSS) --- */}
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-purple-500 relative overflow-hidden">
              <div className="relative z-10">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-1">Spending Credits</p>
                  {/* Added break-all and leading-tight to handle large numbers */}
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 break-all leading-tight">
                      {helpers.formatCurrency(user?.wallet_assets?.credit_balance || 0, 'NGN')}
                  </h3>
                  <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                      <FaCoins /> Available for use
                  </p>
              </div>
              <FaCoins className="absolute -bottom-4 -right-4 text-purple-100 text-6xl z-0" />
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-green-500">
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Earnings Balance</p>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 break-all leading-tight">{helpers.formatCurrency(balance.balance, 'NGN')}</h3>
              <p className="text-xs text-green-600 mt-1">Ready to withdraw</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-blue-500">
              <p className="text-xs font-bold text-gray-500 uppercase">Total Earned</p>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 break-all leading-tight">{helpers.formatCurrency(balance.total_earned, 'NGN')}</h3>
              <p className="text-xs text-blue-600 mt-1">Lifetime earnings</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-yellow-500">
              <p className="text-xs font-bold text-gray-500 uppercase">Pending Payouts</p>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 break-all leading-tight">{helpers.formatCurrency(balance.pending_payouts, 'NGN')}</h3>
              <p className="text-xs text-yellow-600 mt-1">Processing...</p>
          </div>
      </div>

      {/* --- FUND (TOP UP) SPENDING WALLET --- */}
      <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-1">Add money to your Spending Wallet</h3>
          <p className="text-sm text-gray-500 mb-3">
              Your Spending Wallet pays for listings, services and orders. Fund it by bank transfer below.
          </p>
          <BankTransferInfo />
      </div>

      {/* TRANSACTIONS */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="p-4 border-b bg-gray-50 font-bold text-gray-700 flex items-center gap-2">
              <FaHistory /> Transaction History
          </div>
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
              {transactions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No transactions yet.</div>
              ) : (
                  transactions.map(tx => (
                      <div key={tx.id} className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 hover:bg-gray-50 transition-colors">
                          <div className="mb-1 sm:mb-0">
                              <p className="font-bold text-gray-800 text-sm">{tx.description}</p>
                              <div className="flex items-center gap-2 mt-1">
                                  <p className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleString()}</p>
                                  {tx.status === 'held' && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-bold">Escrow Hold</span>}
                              </div>
                          </div>
                          <div className={`font-bold text-sm self-end sm:self-auto ${tx.type === 'credit' || tx.type === 'credit_pending' ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.type.includes('credit') ? '+' : '-'}{helpers.formatCurrency(tx.amount, 'NGN')}
                          </div>
                      </div>
                  ))
              )}
          </div>
      </div>

      {/* WITHDRAW MODAL */}
      {showWithdrawModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fadeInUp max-h-[90vh] overflow-y-auto flex flex-col">
                  <div className="flex justify-between items-center mb-4 border-b pb-2 flex-shrink-0">
                      <h3 className="text-lg font-bold text-gray-800">Request Payout</h3>
                      <button onClick={() => setShowWithdrawModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 mb-4 flex items-start gap-2 flex-shrink-0">
                      <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                      <p>We verify account details instantly. Please ensure the name matches your bank records exactly.</p>
                  </div>

                  <form onSubmit={handleWithdraw} className="space-y-4 flex-grow">
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Amount (NGN)</label>
                          <input 
                              type="number" 
                              required 
                              value={withdrawData.amount}
                              onChange={e => setWithdrawData({...withdrawData, amount: e.target.value})}
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none"
                              placeholder="Min 1,000"
                          />
                      </div>
                      
                      <div className="relative" ref={dropdownRef}>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Select Bank</label>
                          
                          <div 
                              className="w-full p-3 border rounded bg-white flex justify-between items-center cursor-pointer"
                              onClick={() => setIsBankListOpen(!isBankListOpen)}
                          >
                              <span className={withdrawData.bank_name ? "text-gray-800" : "text-gray-400"}>
                                  {withdrawData.bank_name || "-- Choose Bank --"}
                              </span>
                              <FaChevronDown className="text-gray-400 text-xs" />
                          </div>

                          {isBankListOpen && (
                              <div className="absolute z-50 w-full bg-white border border-gray-300 rounded shadow-lg mt-1 max-h-60 overflow-hidden flex flex-col">
                                  <div className="p-2 border-b bg-gray-50">
                                      <div className="flex items-center bg-white border rounded px-2">
                                          <FaSearch className="text-gray-400" />
                                          <input 
                                              type="text"
                                              className="w-full p-2 outline-none text-sm"
                                              placeholder="Search bank..."
                                              value={bankSearchTerm}
                                              onChange={(e) => setBankSearchTerm(e.target.value)}
                                              autoFocus
                                          />
                                      </div>
                                  </div>
                                  <div className="overflow-y-auto flex-grow">
                                      {filteredBanks.length > 0 ? (
                                          filteredBanks.map(bank => (
                                              <div 
                                                  key={bank.id}
                                                  onClick={() => handleSelectBank(bank)}
                                                  className="p-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 border-b border-gray-50 last:border-0"
                                              >
                                                  {bank.name}
                                              </div>
                                          ))
                                      ) : (
                                          <div className="p-4 text-center text-gray-500 text-sm">No banks found.</div>
                                      )}
                                  </div>
                              </div>
                          )}
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Account Number</label>
                          <input 
                              required 
                              value={withdrawData.account_number}
                              onChange={e => setWithdrawData({...withdrawData, account_number: e.target.value})}
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none"
                              placeholder="10-digit NUBAN"
                              maxLength={10}
                          />
                      </div>
                      
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Account Name</label>
                          <input 
                              required 
                              value={withdrawData.account_name}
                              onChange={e => setWithdrawData({...withdrawData, account_name: e.target.value})}
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-green-500 outline-none"
                              placeholder="As it appears on your bank card"
                          />
                      </div>

                      <div className="flex justify-end gap-2 pt-4 border-t mt-auto">
                          <button type="button" onClick={() => setShowWithdrawModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                          <button 
                              type="submit" 
                              disabled={submitting} 
                              className="px-6 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 flex items-center gap-2 shadow-sm"
                          >
                              {submitting ? <FaSpinner className="animate-spin" /> : <FaMoneyBillWave />} 
                              {submitting ? 'Verifying...' : 'Submit Request'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
}

export default WalletDashboard;